'use client'

import React from 'react'
import { useFormStatus } from 'react-dom'
import { cn } from '@/lib/utils'

import { Button, KeyChip, Separator } from '@/components/ui'

interface CardValidationInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  isFirstFourRequired?: boolean
  isLastFourRequired?: boolean
}

export const CardValidationInput = React.forwardRef<
  HTMLInputElement,
  CardValidationInputProps
>(({ className, isFirstFourRequired, isLastFourRequired }, ref) => {
  const { pending } = useFormStatus()
  const firstFourRef = React.useRef<HTMLInputElement>(null)
  const lastFourRef = React.useRef<HTMLInputElement>(null)

  return (
    <div
      className={cn(
        'flex h-[52px] w-full items-center gap-1 overflow-hidden rounded-lg border border-gray-100 bg-white px-1 py-1 ring-gray-700 focus-within:bg-white focus-within:ring-2 dark:border-gray-700 dark:bg-gray-900',
        className,
      )}
      ref={ref}
    >
      {isFirstFourRequired && (
        <input
          type="text"
          placeholder="0000"
          className="placeholder:text-normal h-full w-full bg-transparent px-2 text-center text-sm font-medium placeholder-gray-400 focus-visible:outline-none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={4}
          name="first-four-digits"
          ref={firstFourRef}
          onChange={(e) => {
            if (e.target.value.length === e.target.maxLength) {
              lastFourRef?.current?.focus()
            }
          }}
        />
      )}

      {isFirstFourRequired && (
        <Separator
          className="h-full w-[1px] shrink-0 bg-gray-100"
          orientation="vertical"
        />
      )}

      {isLastFourRequired && (
        <input
          type="text"
          placeholder="0000"
          className="placeholder:text-normal h-full w-full bg-transparent px-2 pl-4 text-center text-sm font-medium placeholder-gray-400 focus-visible:outline-none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={4}
          name="last-four-digits"
          ref={lastFourRef}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && e.currentTarget.value === '') {
              firstFourRef?.current?.focus()
            }
          }}
        />
      )}

      <Button
        disabled={pending}
        aria-disabled={pending}
        className="h-full gap-2 rounded-md pl-4"
        tabIndex={-1}
        type="submit"
      >
        <span>{pending ? 'Submitting' : 'Submit'}</span>
        <KeyChip variant="lighter" shortcut="enter" className="h-full" />
      </Button>
    </div>
  )
})

CardValidationInput.displayName = 'CardValidationInput'
